import { SelectOption } from '@components/select';
import {
  BaseFieldMappingFieldProps,
  FieldMappingData,
  FieldMappingEditorValue,
  transformSimpleValueOutput,
} from '@components/text-editors/addons/field-mapping';
import { SingleField } from '@components/text-editors/addons/single-field';
import { NodeStyledFieldMappingTextEditor } from '@components/text-editors/field-mapping-text-editor';
import { useScopedTranslate } from '@locale/translate';

import { SelectPlaceholder, tag, transformInput } from './extensions';

export type SelectedOptionValue = { value: string; label?: string };

export type FieldMappingSelectFieldValue = FieldMappingEditorValue | SelectedOptionValue;

export type FieldMappingSelectFieldProps = Omit<
  BaseFieldMappingFieldProps<FieldMappingSelectFieldValue>,
  'defaultValue'
> & {
  options: SelectOption[];
  defaultValue?: FieldMappingSelectFieldValue;
} & FieldMappingData;

export const parseEditorOutput = (
  value: FieldMappingEditorValue,
  options: SelectOption[],
): FieldMappingSelectFieldValue | undefined => {
  const hasVariables = value.variables && Object.keys(value.variables).length > 0;
  if (hasVariables) {
    return value;
  }

  const [item] = transformSimpleValueOutput(value, tag);
  if (!item) {
    return undefined;
  }

  const matched = options.find((option) => option.value === item.value);
  if (typeof matched?.label !== 'string') {
    return item;
  }

  return { value: item.value, label: matched.label };
};

export const FieldMappingSelectField = ({
  defaultValue,
  onChange,
  options,
  ...rest
}: FieldMappingSelectFieldProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <NodeStyledFieldMappingTextEditor
      extensions={[SelectPlaceholder, SingleField]}
      plaintext
      singleLine
      onChange={(value) =>
        onChange?.(parseEditorOutput(value, options) as FieldMappingSelectFieldValue)
      }
      defaultValue={defaultValue ? transformInput([defaultValue], options) : undefined}
      placeholder={t('placeholder')}
      disabled
      shouldShowFieldMappingOnFocus
      fieldType="select"
      manualData={options}
      {...rest}
    />
  );
};

const locale = {
  en: {
    fieldMap: 'Field map',
    fieldMapDescription: 'Receive value from a field in journey',
    selectLabel: 'Select value',
    placeholder: 'Select or add',
    selectValue: 'Select value',
    manuallySelectValue: 'Manually select the value',
    add: 'Add',
  },
};
